import Container from 'components/container/container';
import LegalPost from 'components/legal/legal-post';
import Link from 'next/link';

import { LEGAL_MAILTO, LIBUM_URL } from 'shared/constants/globals';
import { LEGAL_DOCS } from 'shared/data/legal-docs';

const DOC = LEGAL_DOCS.find((d) => d.id === 'msa')!;

export default function MSA() {
  const title = DOC.title;
  const description = DOC.description;
  const keywords = DOC.keywords.join(',');
  const url = `${LIBUM_URL}/legal/${DOC.id}}`;

  return (
    <Container title={title} description={description} keywords={keywords} url={url} socialCard="legal">
      <div style={{ width: '100%' }}>
        <LegalPost document={DOC}>
          <p>
            This Master Services Agreement (“<b>Agreement</b>”) is between Libum, LLC, a Texas limited liability company
            (“Libum”) and the party executing an Order (as defined below) that references this Agreement (“
            <b>Customer</b>”) to establish the terms pursuant to which Customer will purchase, and Libum will provide,
            access to Libum&apos;s proprietary software platform. This Agreement is effective as of the date Customer
            signs the initial Order (the “<b>Effective Date</b>”) THE SERVICES AND CUSTOMER&apos;S ACCESS TO AND USE OF
            THE PLATFORM ARE EXPRESSLY CONDITIONED ON CUSTOMER&apos;S ACCEPTANCE OF THIS AGREEMENT, AND CUSTOMER MAY
            ONLY ACCESS AND USE THE PLATFORM, AND LIBUM WILL ONLY PROVIDE THE SERVICES, UPON THE TERMS AND CONDITIONS
            HEREIN. ANY INDIVIDUAL EXECUTING AN ORDER AND AGREEING TO BE BOUND BY THIS AGREEMENT ON BEHALF OF A LEGAL
            ENTITY REPRESENTS AND WARRANTS THAT SUCH INDIVIDUAL HAS THE AUTHORITY TO BIND SUCH ENTITY TO THIS AGREEMENT.
            NOTWITHSTANDING THE FOREGOING, IF THE PARTIES HAVE OTHERWISE AGREED TO A SEPARATE WRITTEN AGREEMENT SIGNED
            BY AUTHORIZED REPRESENTATIVES OF EACH PARTY GOVERNING THE SERVICES AND CUSTOMER&apos;S ACCESS TO AND USE OF
            THE PLATFORM (THE “<b>SERVICES AGREEMENT</b>”), SUCH SERVICES AGREEMENT APPLIES AND THIS AGREEMENT WILL BE
            OF NO FORCE OR EFFECT.
          </p>
          <h1 className="anchor" id="1-definitions">
            <Link href="#1-definitions">1. Definitions</Link>
          </h1>
          <p>
            “<b>Affiliate</b>” means, with respect to a party, any other entity that directly or indirectly controls, is
            controlled by or is under common control with such entity, where “control” means the possession, direct or
            indirect, of the power to direct or cause the direction of the management and policies of such entity
            through the ownership of 50% or more of the outstanding voting securities (but only for as long as such
            entity meets these requirements).
          </p>
          <p>
            “<b>Content</b>” means content, data, and information that is owned by Libum or any of its licensors that is
            provided or made available by Libum through use of the Platform or as part of or in connection with
            Libum&apos;s provision of Services. Content does not include Customer Data.
          </p>
          <p>
            “<b>Customer Data</b>” means the electronic data and information input into the Platform by or on behalf of
            Customer. Customer Data does not include Usage Data or Aggregated Data.
          </p>
          <p>
            “<b>Documentation</b>” means any user materials, instructions, and specifications made available by Libum to
            Customer for the Services.
          </p>
          <p>
            “<b>Implementation Services</b>” means Libum&apos;s standard implementation and set up services for the
            Platform.
          </p>
          <p>
            “<b>Order</b>” means any written order document executed by Libum and Customer setting forth the terms and
            conditions relating to the Services. Each Order is incorporated by reference into this Agreement.
          </p>
          <p>
            “<b>Platform</b>” means Libum&apos;s proprietary platform identified in the applicable Order utilized by
            Libum to provide the Software to Customer under this Agreement. The Platform does not include
            Customer&apos;s connectivity equipment, internet and network connections, hardware, software and other
            equipment as may be necessary for Customer and its Users to connect to and obtain access to the Platform or
            to utilize the Services.
          </p>
          <p>
            “<b>Professional Services</b>” means the professional services provided by Libum as set forth in the
            applicable Order and provided in accordance with this Agreement. Professional Services do not include
            Implementation Services and Support Services.
          </p>
          <p>
            “<b>Services</b>” means, collectively, access to the Platform, Support Services, Implementation Services,
            Professional Services, and the other services made available on, by, or through the Platform by Libum under
            this Agreement.
          </p>
          <p>
            “<b>Software</b>” means Libum&apos;s proprietary software as a service offering and application programming
            interfaces as set forth in the applicable Order and made available through remote access by Libum to
            Customer and Users as part of the Platform, including any modified, updated, or enhanced versions that may
            become part of the Software.
          </p>
          <p>
            “<b>Support Services</b>” means Libum&apos;s standard technical support and software maintenance set forth
            at{' '}
            <Link href="https://libum.io/legal/sla/" passHref legacyBehavior>
              https://libum.io/legal/sla/
            </Link>
            .
          </p>
          <p>
            “<b>Usage Data</b>” means any content, data, or information that is collected or produced by the Platform in
            connection with use of the Services that does not identify Customer or its Users, and may include, but is
            not limited to, usage patterns, traffic logs, and user conduct associated with the Platform.
          </p>
          <p>
            “<b>Users</b>” means Customer&apos;s employees, independent contractors, and other individuals who are
            authorized by Customer to use the Services on behalf of Customer and for whom Customer has purchased access
            to the Platform for.
          </p>
          <h1 className="anchor" id="services">
            <Link href="#services">2. Services</Link>
          </h1>
          <h2 className="anchor" id="provision-of-services">
            <Link href="#provision-of-services">2.1 Provision of Services</Link>
          </h2>
          <p>
            Subject to the terms and conditions of this Agreement, Libum shall provide the Services to Customer and its
            Users in accordance with the Service Level Agreement set forth at{' '}
            <Link href="https://libum.io/legal/sla/" passHref legacyBehavior>
              https://libum.io/legal/sla/
            </Link>{' '}
            (the “<b>SLA</b>”). Libum shall perform the Professional Services for Customer as described in the
            applicable Order. Each Order will contain descriptions of the Professional Services. Customer acknowledges
            that any schedules or timelines for Professional Services set forth in the applicable Order are not fixed
            performance dates and are to be regarded as estimated beginning and completion dates for the Professional
            Services. All Professional Services and Deliverables will be deemed accepted upon delivery.
          </p>
          <h2 className="anchor" id="cooperation">
            <Link href="#cooperation">2.2 Cooperation</Link>
          </h2>
          <p>
            Customer shall supply to Libum the Customer Data along with access and personnel resources that Libum
            reasonably requests in order for Libum to provide the Services. Without limiting the foregoing, Customer
            acknowledges that in order to perform the Professional Services, Libum requires access to certain
            intellectual property, content, data, information, and materials of Customer or Customer&apos;s suppliers
            (collectively, “<b>Customer Materials</b>”). Customer shall provide Libum with the Customer Materials, along
            with any assistance, access, and personnel resources that Libum reasonably requests in order for Libum to
            perform the Professional Services. Customer acknowledges that Libum&apos;s ability to successfully perform
            the Professional Services is contingent upon Libum&apos;s receipt of such Customer Materials and such
            assistance, access, and personnel resources. Libum will not be deemed in breach of this Agreement and will
            have no liability for failure to perform, or any other deficiencies in, the Professional Services or for
            damages resulting from: (a) Customer&apos;s failure to provide any Customer Materials or any such
            assistance, access, and personnel resources; (b) the acts or omissions of Customer, its agents, or
            employees; or (c) performance of the Professional Services in accordance with Customer&apos;s instructions.
          </p>
          <h2 className="anchor" id="resources">
            <Link href="#resources">2.3 Resources</Link>
          </h2>
          <p>
            Customer is solely responsible for, at its own expense, acquiring, installing, and maintaining all
            connectivity equipment, internet and network connections, hardware, software, and other equipment as may be
            necessary for its Users to connect to and access the Platform.
          </p>
          <h2 className="anchor" id="third-party-offerings">
            <Link href="#third-party-offerings">2.4 Third Party Offerings</Link>
          </h2>
          <p>
            Libum may make access to or use of third-party software services, applications, or functionality that link
            to, interoperate with, or are incorporated into the Platform available to Customer (collectively, “
            <b>Third-Party Offerings</b>”). Customer acknowledges that Libum does not own or control such Third-Party
            Offerings, they are made available as a convenience only, and are not part of the Platform or subject to any
            of the warranties, service commitments, or other obligations with respect to Platform under this Agreement
            and that such Third-Party Offerings are subject to their own terms and conditions. Any acquisition by
            Customer of Third-Party Offerings, and any exchange of data between Customer and its Users and any
            Third-Party Offering is solely between Customer and its Users and the applicable Third-Party Offering
            provider. Access to and use of any Third-Party Offering is at Customer&apos;s own risk and is solely
            determined by the relevant third-party provider and is subject to such additional terms and conditions
            applicable to such Third-Party Offering. Libum may disable or restrict access to any Third-Party Offerings
            on the Platform at any time without notice. Libum is not liable for Third-Party Offerings or any Customer
            Data provided to a third party via a Third-Party Offering.
          </p>
          <h1 className="anchor" id="grant-of-rights">
            <Link href="#grant-of-rights">3. Grant of Rights</Link>
          </h1>
          <h2 className="anchor" id="access-rights-customers-use-of-the-platform">
            <Link href="#access-rights-customers-use-of-the-platform">
              3.1 Access Rights; Customer&apos;s Use of the Platform
            </Link>
          </h2>
          <p>
            Subject to the terms and conditions of this Agreement, Libum hereby grants to Customer, during the Term (as
            defined below), a non-exclusive, non-transferable (except as permitted by Section 11.3), non-sublicensable
            right to access and use the Platform for Customer&apos;s and its Affiliates&apos; internal business purposes
            in accordance with the Documentation and the terms and conditions of this Agreement and subject to the
            authorized User limitations set forth in the applicable Order (the “<b>Usage Limitations</b>”). Libum and
            its licensors reserve all rights in and to the Platform and the Services not expressly granted to Customer
            under this Agreement.
          </p>
          <h2 className="anchor" id="restrictions-on-use">
            <Link href="#restrictions-on-use">3.2 Restrictions on Use</Link>
          </h2>
          <p>
            Customer shall not (a) reproduce, display, download, modify, create derivative works of or distribute the
            Platform, or attempt to reverse engineer, decompile, disassemble or access the source code for the Platform
            or any component thereof; (b) use the Platform, or any component thereof, in the operation of a service
            bureau to support or process any content, data, or information of any party other than Customer or Customer
            Affiliates; (c) permit any party, other than the then-currently authorized Users to independently access the
            Platform; (d) use the Platform in any manner or for any purpose that infringes, misappropriates, or
            otherwise violates any intellectual property right or other right of any third-party, or that violates any
            applicable law; (e) exceed the Usage Limitations; or (f) use the Platform to store or transmit any code,
            files, scripts, agents, or programs intended to do harm, including, for example, viruses, worms, time bombs
            and Trojan horses.
          </p>
          <h2 className="anchor" id="users">
            <Link href="#users">3.3 Users</Link>
          </h2>
          <p>
            Under the rights granted to Customer under this Agreement, Customer may permit its and its Affiliates&apos;
            independent contractors and employees to become Users in order to access and use the Platform in accordance
            with this Agreement; provided that Customer will be liable for the acts and omissions of all Customer
            Affiliates and Users to the extent any of such acts or omissions, if performed by Customer, would constitute
            a breach of, or otherwise give rise to liability to Customer under, this Agreement. Customer shall not, and
            shall not permit any User to, use the Platform, Software or Documentation except as expressly permitted
            under this Agreement. Customer is responsible for Users&apos; compliance with this Agreement.
          </p>
          <h2 className="anchor" id="prohibited-data">
            <Link href="#prohibited-data">3.4 Prohibited Data</Link>
          </h2>
          <p>
            Notwithstanding anything to the contrary in this Agreement, Customer shall not, and shall ensure that its
            Users do not, upload to the Platform or otherwise submit or make accessible to Libum any information not
            necessary or expected in the normal course of the credit union industry, including, but not limited to,
            mental or physical health diagnosis, unique biometric data, genetic data, precise geolocation, racial
            origin, ethnic origin, or religious beliefs (collectively, “<b>Prohibited Data</b>”). Notwithstanding
            anything to the contrary in this Agreement, Customer acknowledges that: (i) the Software and Platform are
            not intended for the management or protection of Prohibited Data and may not provide adequate or legally
            required security for Prohibited Data; and (ii) Libum will have no liability for any failure to provide
            protections set forth in any laws, rules, regulations, or standards applicable to such Prohibited Data or to
            otherwise protect the Prohibited Data. If Customer or any Users upload any Prohibited Data to the Platform
            in violation of this Section 3.4, Libum may, without limiting any of its other rights and remedies, delete
            such Prohibited Data.
          </p>
          <h1 className="anchor" id="fees-and-payment-terms">
            <Link href="#fees-and-payment-terms">4. Fees and Payment Terms</Link>
          </h1>
          <h2 className="anchor" id="fees-expenses">
            <Link href="#fees-expenses">4.1 Fees; Expenses</Link>
          </h2>
          <p>
            Customer shall pay Libum the fees set forth on the pricing datasheet in the applicable Order (“<b>Fees</b>”)
            in accordance with the terms of this Agreement. Fees are calculated on the basis of usage. Fees are
            exclusive of, and Customer shall pay all taxes, fees, duties, and other governmental charges arising from
            the payment of any Fees or any amounts owed to Libum under this Agreement (excluding any taxes arising from
            Libum&apos;s income or any employment taxes). Fees for any Services requested by Customer that are not set
            forth in an Order will be charged as mutually agreed to by the parties in writing. Customer shall reimburse
            Libum for all expenses incurred by Libum in connection with the Professional Services and Implementation
            Services.
          </p>
          <h2 className="anchor" id="payment">
            <Link href="#payment">4.2 Payment</Link>
          </h2>
          <p>
            Fees are invoiced monthly in arrears. Except as otherwise provided in the applicable Order, Customer shall
            pay to Libum all Fees within 30 days after Customer&apos;s receipt of the applicable invoice for such Fees
            and expenses. If Customer disagrees with any invoice, Customer must notify Libum of the dispute within 30
            days after receipt of such invoice. If Libum does not receive notice of a dispute during such 30-day period,
            Customer will be deemed to have accepted the Fees and expenses set forth in the invoice. All payments
            received by Libum are non-refundable except as otherwise expressly provided in this Agreement. Customer
            shall make all payments in U.S. dollars.
          </p>
          <h1 className="anchor" id="term-and-termination">
            <Link href="#term-and-termination">5. Term and Termination</Link>
          </h1>
          <h2 className="anchor" id="term">
            <Link href="#term">5.1 Term</Link>
          </h2>
          <p>
            This Agreement commences on the Effective Date and, unless terminated earlier in accordance with this
            Agreement, continues until all Orders have terminated (“<b>Term</b>”).
          </p>
          <h2 className="anchor" id="order-term">
            <Link href="#order-term">5.2 Order Term</Link>
          </h2>
          <p>
            The initial term of an Order begins on the date set forth in the Order and continues for the period set
            forth in the Order (“<b>Initial Term</b>”). Except as otherwise provided in the applicable Order, each Order
            will automatically renew for additional periods equal to the length of the Initial Term (each, a “
            <b>Renewal Term</b>”), unless a party gives the other party written notice of its intent to not renew at
            least 30 days prior to the end of the Initial Term or the then-current Renewal Term.
          </p>
          <h2 className="anchor" id="termination-for-cause">
            <Link href="#termination-for-cause">5.3 Termination for Cause</Link>
          </h2>
          <p>
            A party may terminate this Agreement or an Order upon notice if the other party breaches any material
            provision of this Agreement and (provided that such breach is capable of cure) does not cure such breach
            within 30 days after being provided with written notice of such breach.
          </p>
          <h2 className="anchor" id="effects-of-termination">
            <Link href="#effects-of-termination">5.4 Effects of Termination</Link>
          </h2>
          <p>
            Upon termination of this Agreement and all Orders: (a) all amounts owed to Libum under this Agreement before
            such termination will be due and payable in accordance with Section 4; (b) Customer&apos;s rights granted in
            this Agreement will immediately cease; (c) Customer shall promptly discontinue all access and use of the
            Platform and return or erase, all copies of the Documentation in Customer&apos;s possession or control; and
            (d) Libum shall promptly return or erase all Customer Data, except that Libum may retain Customer Data in
            Libum&apos;s archived backup files. Sections 4, 5.4, 6, 7.4, 8, 9, 10, and 11 of this Agreement survive
            expiration or termination of this Agreement.
          </p>
          <h2 className="anchor" id="suspension">
            <Link href="#suspension">5.5 Suspension</Link>
          </h2>
          <p>
            Notwithstanding anything to the contrary in this Agreement, Libum may suspend Customer&apos;s access to the
            Platform if Libum determines that: (a) there is an attack on the Platform; (b) Customer&apos;s or any of its
            Users&apos; use of the Platform poses a reasonable risk of harm or liability to Libum and, if capable of
            being cured, Customer is not taking appropriate action to cure such risk; (c) Customer has breached Sections
            3.2 or 10; (d) Customer&apos;s or its Users&apos; use of the Platform violates applicable law; or (e)
            Customer has failed to pay any undisputed amounts owed under this Agreement when due and has failed to cure
            such late payment within 15 days after Libum has provided Customer with written notice of such late payment.
            Libum shall use commercially reasonable efforts to provide Customer with notice of such suspension. Libum
            may suspend Customer&apos;s access to the Platform until the situation giving rise to the suspension has
            been remedied to Libum&apos;s reasonable satisfaction. Libum&apos;s suspension of Customer&apos;s access to
            the Platform will not relieve Customer of its payment obligations under this Agreement and Client will not
            be eligible for any Service Credits the SLA.
          </p>
          <h1 className="anchor" id="proprietary-rights">
            <Link href="#proprietary-rights">6. Proprietary Rights</Link>
          </h1>
          <h2 className="anchor" id="customer-data">
            <Link href="#customer-data">6.1 Customer Data</Link>
          </h2>
          <p>
            As between the parties, Customer owns all right, title, and interest in Customer Data, including all
            intellectual property rights therein.
          </p>
          <h2 className="anchor" id="customer-data-license-grant">
            <Link href="#customer-data-license-grant">6.2 Customer Data License Grant</Link>
          </h2>
          <p>
            Customer hereby grants to Libum and its authorized representatives and contractors, during the Term, a
            limited, non-exclusive, non-transferable (except as permitted by Section 11.3) license to use the Customer
            Data solely for the limited purpose of performing the Services for Customer and fulfilling its other
            obligations and exercising its rights under this Agreement.
          </p>
          <h2 className="anchor" id="the-services">
            <Link href="#the-services">6.3 The Services</Link>
          </h2>
          <p>
            All proprietary technology utilized by Libum to perform its obligations under this Agreement, and all
            intellectual property rights in and to the foregoing, as between the parties, are the exclusive property of
            Libum. Libum or its third-party licensors retain ownership of all right, title, and interest to all
            copyrights, patents, trademarks, trade secrets, and other intellectual property rights in and to the Content
            and the Platform, including without limitation the Software, Documentation, customizations, modifications,
            and enhancements, and all processes, know-how, and the like utilized by or created by Libum in performing
            under this Agreement. Any rights not expressly granted to Customer hereunder are reserved by Libum.
          </p>
          <h2 className="anchor" id="aggregated-data">
            <Link href="#aggregated-data">6.4 Aggregated Data</Link>
          </h2>
          <p>
            Notwithstanding anything in this Agreement to the contrary, Libum may analyze Customer Data to create a
            de-identified and aggregated data set that does not identify Customer or its Users (collectively,
            “Aggregated Data”). Libum retains ownership of all right, title, and interest in and to Aggregated Data.
            Libum may use Aggregated Data for any lawful purpose, including, but not limited to, to improve, market, and
            provide the Services.
          </p>
          <h2 className="anchor" id="usage-data">
            <Link href="#usage-data">6.5 Usage Data</Link>
          </h2>
          <p>
            Libum retains ownership of all right, title, and interest in and to the Usage Data. Libum may use Usage Data
            in connection with its performance of its obligations in this Agreement and for any other lawful business
            purpose, including, but not limited to, benchmarking, data analysis, and to improve Libum&apos;s services,
            systems, and algorithms.
          </p>
          <h2 className="anchor" id="deliverables-license-to-libum-background-intellectual-property">
            <Link href="#deliverables-license-to-libum-background-intellectual-property">
              6.6 Deliverables; License to Libum Background Intellectual Property
            </Link>
          </h2>
          <p>
            The parties acknowledge and agree that Customer will own all Deliverables, and, subject to Customer&apos;s
            payment of all applicable Fees, Libum hereby assigns to Customer all right, title, and interest worldwide in
            and to such Deliverables, including all intellectual property rights therein, but excluding Libum Background
            Intellectual Property (as defined below). “<b>Deliverable</b>” means any item created specifically and
            exclusively for and delivered to Customer by or on behalf of Libum under this Agreement in connection with
            the Professional Services and identified as a Deliverable in the applicable Order. Notwithstanding anything
            to the contrary in this Agreement, as between the parties, Libum owns all: (a) software, tools, routines,
            programs, content, photographs, designs, technology, intellectual property, ideas, know-how, processes,
            techniques, and inventions that Libum uses, makes, develops, or reduces to practice, whether alone or
            jointly with others or otherwise obtains: (i) prior to this Agreement; (ii) independently of or outside the
            scope of the Professional Services; or (iii) that has applicability to Libum&apos;s provision of services to
            its customers generally; (b) all enhancements, modifications, improvements and derivative works of each and
            any of the foregoing; and (c) all copyrights, trademarks, service marks, trade secrets, patents, patent
            applications, and other proprietary rights related to each and any of the foregoing (collectively, the “
            <b>Libum Background Intellectual Property</b>”). Subject to the terms and conditions of this Agreement Libum
            hereby grants to Customer a non-exclusive, perpetual, worldwide, right and license, with the right to
            sublicense, under all of Libum&apos;s intellectual property rights, to all Libum Background Intellectual
            Property incorporated into any Deliverable, to use, reproduce, distribute, publicly display, publicly
            perform, and create derivative works of such Libum Background Intellectual Property solely to the extent
            reasonably required in connection with Customer&apos;s use of the Deliverable on the condition that Customer
            does not: (a) reproduce, distribute, or use Libum Background Intellectual Property other than as components
            of the Deliverable; or (b) sublicense any rights in Libum Background Intellectual Property other than in
            support of Customer&apos;s internal business purposes. For the avoidance of doubt, the license grant in this
            Section 6.6 does not include the Software or the right to access and use the Platform, for which access is
            granted solely pursuant to Section 3.1. In addition, and notwithstanding anything to the contrary in this
            Agreement, Deliverables exclude, and Libum retains ownership of all right, title, and interest in and to,
            any component of, or modification, customization, or enhancement of, the Platform and the Software.
          </p>
          <h1 className="anchor" id="warranty-disclaimers">
            <Link href="#warranty-disclaimers">7. Warranty; Disclaimers</Link>
          </h1>
          <h2 className="anchor" id="access-to-the-platform">
            <Link href="#access-to-the-platform">7.1 Access to the Platform</Link>
          </h2>
          <p>
            Libum warrants that the Platform will perform materially in accordance with the Documentation and this
            Agreement. Libum does not warrant that the Platform will be completely error-free or uninterrupted. If
            Customer notifies Libum of a reproducible error in the Platform that indicates a breach of the foregoing
            warranty (each, an “<b>Error</b>”) within 30 days after Customer experiences such Error, Libum shall, at its
            own expense and as its sole obligation and Customer&apos;s exclusive remedy (except for Service Credits that
            Customer may be entitled to receive due to Platform unavailability under the SLA): (a) use commercially
            reasonable efforts to correct or provide a workaround for such Error; or (b) if Libum is unable to correct
            or provide a workaround for such Error within 60 days after receiving notice of such Error from Customer,
            Customer may terminate this Agreement upon notice to Libum and, Libum shall refund the amounts paid by
            Customer for access to the Platform for the period during which the Platform was not usable by Customer. The
            warranties set forth in this Section 7.1 do not apply to any Third Party Offerings or cover any Error caused
            by: (i) Customer or its Users; (ii) use of the Platform in any manner or in any environment inconsistent
            with its intended purpose; (iii) Customer&apos;s hardware or software if modified or repaired in any manner
            which materially adversely affects the operation or reliability of the Platform, or (iv) any equipment,
            software, or other material utilized by Customer in connection with the Platform contrary to the
            provider&apos;s instructions.
          </p>
          <h2 className="anchor" id="professional-services-and-deliverables">
            <Link href="#professional-services-and-deliverables">7.2 Professional Services and Deliverables</Link>
          </h2>
          <p>
            Libum warrants to Customer that: (a) the Professional Services and Implementation Services will be performed
            in a professional manner, materially in accordance with the applicable scope of work agreed to pursuant to
            the applicable Order; and (b) the Deliverables, when delivered, will materially conform to the
            specifications set forth in the applicable scope of work agreed to pursuant to the applicable Order. If
            Customer notifies Libum of a breach of the foregoing warranty specifying the breach in reasonable detail
            within 30 days after Libum performs the Services or delivers the Deliverable, Libum shall, at its own
            expense and as its sole obligation and Customer&apos;s exclusive remedy for breach of the foregoing
            warranty: (i) use commercially reasonable efforts to re-perform the Services or re-deliver the Deliverable
            (as applicable) which gave rise to the breach; or (ii) if Libum cannot re-perform such defective Services or
            re-deliver such defective Deliverable (as applicable) to Customer within 30 days after receiving notice of
            the breach, Customer may terminate this Agreement upon written notice to Libum, and Libum shall refund to
            Customer the fees paid for such defective Services and Deliverables.
          </p>
          <h2 className="anchor" id="right-to-customer-data">
            <Link href="#right-to-customer-data">7.3 Right to Customer Data</Link>
          </h2>
          <p>
            Customer represents and warrants that it has the right to: (a) use the Customer Data as contemplated by this
            Agreement; and (b) grant Libum the license in Section 6.2.
          </p>
          <h2 className="anchor" id="disclaimer">
            <Link href="#disclaimer">7.4 Disclaimer</Link>
          </h2>
          <p>
            EXCEPT AS EXPRESSLY PROVIDED IN THIS SECTION 7, NEITHER PARTY MAKES ANY WARRANTIES OF ANY KIND AND EACH
            PARTY SPECIFICALLY DISCLAIMS ALL OTHER WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING,
            WITHOUT LIMITATION, ALL IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE,
            NON-INFRINGEMENT, AND ANY WARRANTIES ARISING FROM COURSE OF DEALING OR COURSE OF PERFORMANCE.
          </p>
          <h1 className="anchor" id="indemnification">
            <Link href="#indemnification">8. Indemnification</Link>
          </h1>
          <h2 className="anchor" id="claims-against-customer">
            <Link href="#claims-against-customer">8.1 Claims Against Customer</Link>
          </h2>
          <p>
            Libum shall defend any claim, suit, or action against Customer brought by a third party to the extent based
            on an allegation that the Software infringes any intellectual property rights of such third party (a “
            <b>Customer Claim</b>”), and Libum shall indemnify and hold Customer harmless, from and against damages,
            losses, liabilities, and expenses (including reasonable attorneys&apos; fees and other legal expenses)
            (collectively, “<b>Losses</b>”) that are specifically attributable to such Customer Claim or those costs and
            damages agreed to in a settlement of such Customer Claim. The foregoing obligations are conditioned on
            Customer: (a) promptly notifying Libum in writing of such Customer Claim; (b) giving Libum sole control of
            the defense thereof and any related settlement negotiations; and (c) cooperating and, at Libum&apos;s
            request and expense, assisting in such defense. In the event that the use of the Platform is enjoined, Libum
            shall, at its option and at its own expense either: (i) procure for Customer the right to continue using the
            Platform; (ii) replace the Software with a non-infringing but functionally equivalent product; (iii) modify
            the Software so it becomes non-infringing; or (iv) terminate this Agreement and refund the amounts Customer
            paid for access to the Platform that relate to the period during which Customer was not able to use the
            Platform. Notwithstanding the foregoing, Libum will have no obligation under this Section 8.1 with respect
            to any infringement claim based upon: (1) any use of the Platform not in accordance with this Agreement; (2)
            any use of the Platform in combination with products, equipment, software, or data that Libum did not supply
            or approve of if such infringement would have been avoided without the combination with such other products,
            equipment, software or data; (3) any modification of the Platform by any person other than Libum or its
            authorized agents or subcontractors; or (4) any Third-Party Offering. This Section 8.1 states Libum&apos;s
            entire liability and Customer&apos;s sole and exclusive remedy for infringement claims or actions.
          </p>
          <h2 className="anchor" id="claims-against-libum">
            <Link href="#claims-against-libum">8.2 Claims Against Libum</Link>
          </h2>
          <p>
            Customer shall defend, any claim, suit, or action against Libum brought by a third party to the extent that
            such claim, suit, or action is based upon Libum&apos;s use of any Customer Data in accordance with this
            Agreement or Customer&apos;s use of any Customer Data (a “<b>Libum Claim</b>”) and Customer shall indemnify
            and hold Libum harmless, from and against Losses that are specifically attributable to such Libum Claim or
            those costs and damages agreed to in a settlement of such Libum Claim. The foregoing obligations are
            conditioned on Libum: (a) promptly notifying Customer in writing of such Libum Claim; (b) giving Customer
            sole control of the defense thereof and any related settlement negotiations; and (c) cooperating and, at
            Customer&apos;s request and expense, assisting in such defense. Notwithstanding the foregoing, Customer will
            have no obligation under this Section 8.2 or otherwise with respect to any Libum Claim to the extent based
            upon Libum&apos;s use of the Customer Data in violation of this Agreement.
          </p>
          <h1 className="anchor" id="limitations-of-liability">
            <Link href="#limitations-of-liability">9. Limitations of Liability</Link>
          </h1>
          <p>
            IN NO EVENT WILL EITHER PARTY BE LIABLE FOR ANY CONSEQUENTIAL, INDIRECT, EXEMPLARY, SPECIAL, OR INCIDENTAL
            DAMAGES, OR FOR ANY LOST DATA, LOST PROFITS, OR COSTS OF PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES,
            ARISING FROM OR RELATING TO THIS AGREEMENT, HOWEVER CAUSED AND UNDER ANY THEORY OF LIABILITY (INCLUDING
            NEGLIGENCE), EVEN IF SUCH PARTY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. EACH PARTY&apos;S TOTAL
            CUMULATIVE LIABILITY IN CONNECTION WITH THIS AGREEMENT, WHETHER IN CONTRACT OR TORT OR OTHERWISE, WILL NOT
            EXCEED THE AMOUNT OF FEES PAID OR OWED BY CUSTOMER TO LIBUM UNDER THIS AGREEMENT DURING THE INITIAL TERM OR
            RENEWAL TERM, AS THE CASE MAY BE, DURING WHICH THE EVENTS GIVING RISE TO SUCH LIABILITY OCCURRED. THE
            EXCLUSIONS AND LIMITATION OF LIABILITIES SET FORTH IN THIS SECTION 9 DO NOT APPLY TO A PARTY&apos;S
            OBLIGATIONS UNDER SECTION 8, TO LIABILITY ARISING FROM A PARTY&apos;S BREACH OF SECTION 10, OR TO LIABILITY
            ARISING FROM CUSTOMER&apos;S BREACH OF SECTION 3.2.
          </p>
          <h1 className="anchor" id="confidentiality">
            <Link href="#confidentiality">10. Confidentiality</Link>
          </h1>
          <h2 className="anchor" id="10-definitions">
            <Link href="#definitions">10.1 Definitions</Link>
          </h2>
          <p>
            “<b>Confidential Information</b>” means all information disclosed by one party (“<b>Discloser</b>”) to the
            other party (“<b>Recipient</b>”) under this Agreement during the Term. Confidential Information includes
            information that is marked or identified as confidential and, if not marked or identified as confidential,
            information that should reasonably have been understood by Recipient to be proprietary and confidential to
            Discloser or to a third party. Libum&apos;s Confidential Information includes Software and Documentation.
            Customer&apos;s Confidential Information includes Customer Data.
          </p>
          <h2 className="anchor" id="protection">
            <Link href="#protection">10.2 Protection</Link>
          </h2>
          <p>
            Recipient shall not use any Confidential Information for any purpose not expressly permitted by this
            Agreement and shall not disclose Confidential Information to anyone other than Recipient&apos;s employees
            and independent contractors who have a need to know such Confidential Information for purposes of this
            Agreement and who are subject to confidentiality obligations no less restrictive than Recipient&apos;s
            obligations under this Section 10. Recipient will be liable to the Discloser for any of its employees&apos;
            and independent contractors&apos; acts or omissions, which, if performed by Recipient, would constitute a
            breach of this Section 10. Recipient shall protect Confidential Information from unauthorized use, access,
            and disclosure in the same manner as Recipient protects its own confidential or proprietary information of a
            similar nature and with no less than reasonable care.
          </p>
          <h2 className="anchor" id="exceptions">
            <Link href="#exceptions">10.3 Exceptions</Link>
          </h2>
          <p>
            Recipient will have no confidentiality obligations under Section 10.2 above with respect to any information
            of Discloser that Recipient can document: (a) was already known to Recipient prior to Discloser&apos;s
            disclosure; (b) is disclosed to Recipient by a third party who had the right to make such disclosure without
            violating any confidentiality agreement with or other obligation to the party who disclosed the information;
            (c) is, or through no fault of Recipient has become, generally available to the public; or (d) is
            independently developed by Recipient without access to or use of Confidential Information. Recipient may
            disclose Confidential Information if required to as part of a judicial process, government investigation,
            legal proceeding, or other similar process on the condition that, to the extent permitted by applicable law,
            Recipient gives prior written notice of such requirement to Discloser. Recipient shall take reasonable
            efforts to provide this notice in sufficient time to allow Discloser to seek an appropriate confidentiality
            agreement, protective order, or modification of any disclosure, and Recipient shall reasonably cooperate in
            such efforts at the expense of Discloser.
          </p>
          <h1 className="anchor" id="general">
            <Link href="#general">11. General</Link>
          </h1>
          <h2 className="anchor" id="indepentent-contractors">
            <Link href="#indepentent-contractors">11.1 Independent Contractor</Link>
          </h2>
          <p>
            The relationship of the parties established under this Agreement is that of independent contractors and
            neither party is a partner, employee, agent, or joint venture partner of or with the other, and neither
            party has the right or authority to assume or create any obligation on behalf of the other party.
          </p>
          <h2 className="anchor" id="subcontractors">
            <Link href="#subcontractors">11.2 Subcontractors</Link>
          </h2>
          <p>
            Libum may utilize subcontractors, subprocessors, and other third-party service providers (collectively, “
            <b>Subcontractors</b>”) in the performance of its obligations, provided that Libum will remain liable and
            responsible for the Subcontractors&apos; acts and omissions to the extent any of such acts or omissions, if
            performed by Libum, would constitute a breach of, or otherwise give rise to liability to Libum under, this
            Agreement when they are performing for or on behalf of Libum.
          </p>
          <h2 className="anchor" id="assignment">
            <Link href="#assignment">11.3 Assignment</Link>
          </h2>
          <p>
            Neither party may assign this Agreement or any of its rights under this Agreement to any third party without
            the other party&apos;s prior written consent; except that a party may assign this Agreement without consent
            from the other party to (a) an Affiliate; or (b) any successor to its business or assets to which this
            Agreement relates, whether by merger, acquisition, or sale of all or substantially all of its assets, or
            otherwise. Any attempted assignment in violation of the foregoing will be void and of no force or effect.
          </p>
          <h2 className="anchor" id="force-majeure">
            <Link href="#force-majeure">11.4 Force Majeure</Link>
          </h2>
          <p>
            Except for payment obligations, neither party will be liable for any breach of this Agreement, or for any
            delay or failure of performance, resulting from any cause beyond that party&apos;s reasonable control.
          </p>
          <h2 className="anchor" id="notices">
            <Link href="#notices">11.5 Notices</Link>
          </h2>
          <p>
            To be effective, notices under this Agreement must be delivered: (a) via email; and (b) in writing by
            courier, or certified or registered mail (postage prepaid and return receipt requested) to the other party
            at the address for each party set forth below. Any notice will be effective upon the other party&apos;s
            receipt of both notices.
          </p>
          <ul>
            <li>
              <span className="underline">If to Libum:</span>
              <ul>
                <li>Libum, LLC</li>
                <li>Attention: Legal</li>
                <li>1401 Lavaca St. PMB 41479</li>
                <li>Austin, TX 78701</li>
                <li>
                  <Link href={LEGAL_MAILTO}>legal@libum.io</Link>
                </li>
              </ul>
            </li>
            <li>
              <span className="underline">If to Customer:</span>
              <ul>
                <li>The physical address and email address that Libum has on file for the Customer.</li>
              </ul>
            </li>
          </ul>
          <h2 className="anchor" id="governing-law-venue">
            <Link href="#governing-law-venue">11.6 Governing Law; Venue</Link>
          </h2>
          <p>
            The laws of the State of Texas govern this Agreement and any matters related to this Agreement, without
            regard to any conflicts of laws principles that would require the application of the laws of a different
            jurisdiction. The parties hereby submit to the exclusive jurisdiction of, and waive any venue objections
            against, state or federal courts sitting in Austin, Texas in any litigation arising out of this Agreement or
            the Services.
          </p>
          <h2 className="anchor" id="remedies">
            <Link href="#remedies">11.7 Remedies</Link>
          </h2>
          <p>
            Each party acknowledges that any actual or threatened breach of Sections 3.2 or 10 will constitute
            immediate, irreparable harm to the non-breaching party for which monetary damages would be an inadequate
            remedy, that injunctive relief is an appropriate remedy for such breach, and that if granted, the breaching
            party agrees to waive any bond that would otherwise be required. If any legal action is brought by a party
            to enforce this Agreement, the prevailing party will be entitled to receive its attorneys’ fees, court
            costs, and other legal expenses, in addition to any other relief it may receive from the non-prevailing
            party.
          </p>
          <h2 className="anchor" id="compliance-with-laws">
            <Link href="#compliance-with-laws">11.8 Compliance with Laws</Link>
          </h2>
          <p>
            Each party shall comply with all laws, rules, and regulations, applicable to that party in connection with
            this Agreement.
          </p>
          <h2 className="anchor" id="waivers">
            <Link href="#waivers">11.9 Waivers</Link>
          </h2>
          <p>
            To be effective, any waivers must be in writing and signed by the party to be charged. Any waiver or failure
            to enforce any provision of this Agreement on one occasion will not be deemed a waiver of any other
            provision or of such provision on any other occasion.
          </p>
          <h2 className="anchor" id="severability">
            <Link href="#severability">11.10 Severability</Link>
          </h2>
          <p>
            If any provision of this Agreement is unenforceable, the other provisions of this Agreement will be
            unimpaired, and the unenforceable provision will be deemed modified so that it is enforceable to the maximum
            extent permitted by law (unless such modification is not permitted by law, in which case such provision will
            be disregarded).
          </p>
          <h2 className="anchor" id="data-processing-addendum">
            <Link href="#data-processing-addendum">11.11 Data Processing Addendum</Link>
          </h2>
          <p>
            Libum shall protect and process any Customer Personal Data received from Customer in accordance with the
            Data Processing Addendum set forth at{' '}
            <Link href="https://libum.io/legal/data/" passHref legacyBehavior>
              https://libum.io/legal/data/
            </Link>
            .
          </p>
          <h2 className="anchor" id="modification">
            <Link href="#modification">11.12 Modification</Link>
          </h2>
          <p>
            Libum may modify this Agreement at any time, upon written notice to Customer. Any such modification will
            become effective with respect to the applicable Order upon the commencement of the Renewal Term for such
            Order following the date of such notice. If Customer does not agree to the modified Agreement, Customer may
            choose to not renew the then-current Initial Term or Renewal Term (as applicable). Except as otherwise set
            forth in this Section, this Agreement may not otherwise be modified except by a written amendment signed by
            an authorized representative of each party.
          </p>
          <h2 className="anchor" id="entire-agreement">
            <Link href="#entire-agreement">11.13 Entire Agreement</Link>
          </h2>
          <p>
            This Agreement, including any Order and any exhibits or attachments thereto, constitutes the final and
            entire agreement between the parties regarding the subject hereof and supersedes all other agreements,
            whether written or oral, between the parties concerning such subject matter. No terms and conditions
            proposed by either party will be binding on the other party unless accepted in writing by both parties, and
            each party hereby objects to and rejects all terms and conditions not so accepted. To the extent of any
            conflict between the provisions of this Agreement and the provisions of any Order, the provisions of the
            Agreement will govern unless the Order specifically overrides this Agreement.
          </p>
        </LegalPost>
      </div>
    </Container>
  );
}
